import {MarkableElement} from "../misc/markable-element";
import {MarkerInfo} from "../misc/marker-info.interface";
import React, {MutableRefObject} from "react";
import {logError} from "./logger.service";
import {enqueueSnackbar} from "notistack";
import {renderReactNode} from "./dom-helper.service";

// Global registry to track all event listeners for each marker element, this is much easier than trying to tie each listener to the marker itself
const elementEventListeners = new WeakMap<HTMLElement, Array<{type: string, listener: EventListener}>>();

const addTrackedEventListener = (element: HTMLElement, type: string, listener: EventListener) => {
  const existingListeners = elementEventListeners.get(element) || [];
  existingListeners.push({type, listener});
  elementEventListeners.set(element, existingListeners);
  element.addEventListener(type, listener);
};

const removeAllTrackedEventListeners = (element: HTMLElement, type?: string) => {
  const listeners = elementEventListeners.get(element) || [];
  const listenersToRemove = type ? listeners.filter(l => l.type === type) : listeners;
  const listenersToKeep = type ? listeners.filter(l => l.type !== type) : [];
  
  listenersToRemove.forEach(({type: listenerType, listener}) => {
    element.removeEventListener(listenerType, listener);
  });
  
  elementEventListeners.set(element, listenersToKeep);
};

const getMatchingElementOrThrow = <T extends MarkableElement>(elements: T[], elementId: number): T => {
  const element = elements.find(e => e.id === elementId);
  if(!element) {
    logError('Attempted to update a marker without associated element data')
    enqueueSnackbar('Attempted to update a marker without associated element data, please reload the page', {variant: 'error', autoHideDuration: 5000});
    throw new Error('Attempted to update a marker without associated element data');
  }
  return element;
}

const getMarkerId = (elementType: string, elementId: number) => `maps-marker-${elementType}-${elementId}`;

const detachMarker = (marker: google.maps.marker.AdvancedMarkerElement) => {
  // AdvancedMarkerElement must be detached via map=null; Element.remove() can leave ghosts on the map.
  marker.map = null;
};

const cleanLostMarkers = (page: Document, elementType: string, markers: MarkerInfo[]) => {
  const lostMarkers = markers.filter(m => {
    const markerId = getMarkerId(elementType, m.elementId);
    return !page.getElementById(markerId);
  });
  lostMarkers.forEach(m => {
    detachMarker(m.markerDomObject);
  });
  return markers.filter(m => !lostMarkers.some(lm => lm.elementId === m.elementId));
}

/**
 * Keep an authoritative markersRef so overlapping syncs (projection_changed + zoom + data
 * effects) don't each create a fresh AdvancedMarkerElement for the same element id.
 * React state alone is too stale for that; spiderfy makes the orphaned copies obvious.
 */
export const syncMarkers = <T extends MarkableElement>(
  page: Document,
  map: google.maps.Map,
  scale: number | undefined,
  elementType: string,
  elements: T[],
  markersRef: MutableRefObject<MarkerInfo[]>,
  setMarkers: (markers: MarkerInfo[]) => void,
  labelProducer: (element: T) => string,
  pinProducer: (element: T) => React.ReactNode,
  onClick: (element: T) => void,
  onDragEnd: (element: T, event: google.maps.MapMouseEvent) => void,
  markerOptions: Pick<google.maps.marker.AdvancedMarkerElementOptions, "gmpClickable" | "gmpDraggable"> | undefined = undefined,
  onDrag?: (element: T, event: google.maps.MapMouseEvent) => void,
  /** Extra signature (e.g. rotation heading) that should also trigger a content re-render, beyond label/scale. */
  contentKeyProducer?: (element: T) => string
)=> {
  // Sometimes google maps seems to lose markers around re-renders. This whole synchronization paradigm relies on the list of markers in state matching the markers on the map, so we need to remove any markers that don't exist on the map.
  const cleanedMarkers = cleanLostMarkers(page, elementType, markersRef.current);

  const markersToRemove = cleanedMarkers.filter(m => !elements.some(e => e.id === m.elementId));
  const markersToKeep = cleanedMarkers.filter(m => elements.some(e => e.id === m.elementId));
  const elementsWithoutMarkers = elements.filter(e => !markersToKeep.some(m => m.elementId === e.id));

  markersToRemove.forEach(m => detachMarker(m.markerDomObject));
  markersToKeep.forEach(m => {
    const matchingElement = getMatchingElementOrThrow(elements, m.elementId);
    const markerMoved = m.markerDomObject.position?.lng !== matchingElement.lng || m.markerDomObject.position?.lat !== matchingElement.lat;
    if(markerMoved) {
      m.markerDomObject.position = {lat: matchingElement.lat, lng: matchingElement.lng};
    }
    
    // Update gmpDraggable if markerOptions are provided
    if(markerOptions !== undefined) {
      const newDraggable = markerOptions.gmpDraggable !== undefined ? markerOptions.gmpDraggable : true;
      if(m.markerDomObject.gmpDraggable !== newDraggable) {
        m.markerDomObject.gmpDraggable = newDraggable;
      }
    }
  });

  const newMarkers: MarkerInfo[] = elementsWithoutMarkers.map((element) => {
    const content = renderReactNode(pinProducer(element));
    const options = {
      position: {lat: element.lat, lng: element.lng},
      map: map,
      title: labelProducer(element),
      gmpDraggable: markerOptions?.gmpDraggable !== undefined ? markerOptions.gmpDraggable : true,
      gmpClickable: markerOptions?.gmpClickable !== undefined ? markerOptions.gmpClickable : true,
      content
    };
    const marker = new google.maps.marker.AdvancedMarkerElement(options);
    marker.id = getMarkerId(elementType, element.id);
    return {
      elementId: element.id,
      markerDomObject: marker,
      previousLabel: undefined,
      clickEventListener: undefined,
      scale
    }
  });
  const finalMarkers = [...markersToKeep, ...newMarkers].sort((a, b) => a.elementId - b.elementId);

  const finalMarkersWithListeners = finalMarkers.map(marker => {
    const element = getMatchingElementOrThrow(elements, marker.elementId);
    // Re-render the marker content if the label, scale, or content key has changed TODO: Now that we're doing pinProducer based on element, this doesn't guarantee that all re-renders will be correct
    const markerLabel = labelProducer(element);
    const contentKey = contentKeyProducer ? contentKeyProducer(element) : undefined;
    if(marker.previousLabel !== markerLabel || marker.scale !== scale || marker.previousContentKey !== contentKey) {
      marker.markerDomObject.content = renderReactNode(pinProducer(element));
      marker.previousLabel = markerLabel;
      marker.scale = scale;
      marker.previousContentKey = contentKey;
    }
    return markerWithListeners(marker, element, onClick, onDragEnd, onDrag);
  });

  // Update the ref synchronously so a second sync in the same tick reuses these markers.
  markersRef.current = finalMarkersWithListeners;
  setMarkers(finalMarkersWithListeners);
}

const markerWithListeners = <T extends MarkableElement>(
  marker: MarkerInfo,
  element: T,
  onClick: (element: T) => void,
  onDragEnd: (element: T, event: google.maps.MapMouseEvent) => void,
  onDrag?: (element: T, event: google.maps.MapMouseEvent) => void
) => {
  /* Google maps provides a wrapped, simplified API for events, and we're supposed to be able to use those without having to worry about cross browser compatibility.
  There's only one small problem: It's not fully cross browser compatible! Specifically the gmp-click event doesn't work on Safari, and the older click event doesn't work on mobile in general.

  So, instead we use this nasty hack of adding a pointerup listener directly to the underlying dom element, bypassing the google maps event system entirely.
  However, this creates a new problem: The pointerup event also triggers the dragend event from the google maps event system which we also are listening to (and which thankfully does work on all browsers).
  To handle this, we need to check if we're in the middle of dragging, and if we are, we don't trigger the click event.
  This means we have to add a dragstart listener for no other reason that to set a flag when we're dragging, and clear it when we're done dragging.

  But then we get to another exciting browser specific problem, on Chrome only ANY pointer up event also triggers a dragstart event, so on Chrome the isDragging flag will always be true.
  To solve this, we need to check if the pointerup event is in the same position as the dragstart event, and if it is, we don't trigger the click event.
  Since this only needs to work for Chrome mobile, we only need to set the initial drag location for touch events, for any other browser the isDragging flag will be false and the click event will trigger as expected.
  */
  let isDragging = false;
  let initialDragPosition: {pageX: number, pageY: number} | undefined = undefined
  const onDragStartListener = (event: google.maps.MapMouseEvent) =>  {
    const touches = ((event.domEvent as TouchEvent)?.touches ?? [])[0];
    if(touches) {
      initialDragPosition = {pageX: touches.pageX, pageY: touches.pageY};
    } else {
      const {pageX, pageY} = (event.domEvent as PointerEvent);
      initialDragPosition = {pageX, pageY};
    }
    isDragging = true;
  };
  google.maps.event.clearListeners(marker.markerDomObject, 'dragstart');
  marker.markerDomObject.addListener('dragstart', onDragStartListener);

  google.maps.event.clearListeners(marker.markerDomObject, 'drag');
  if (onDrag) {
    marker.markerDomObject.addListener('drag', (event: google.maps.MapMouseEvent) => {
      onDrag(element, event);
    });
  }

  const onDragEndListener = (event: google.maps.MapMouseEvent) =>  {
    isDragging = false;
    onDragEnd(element, event);
  };
  google.maps.event.clearListeners(marker.markerDomObject, 'dragend');
  marker.markerDomObject.addListener('dragend', onDragEndListener);

  const maximumClickDragDelta = 5;
  const onClickListener = (e: PointerEvent) =>  {
    const isWithinClickDragDelta = Math.abs(e.pageX - (initialDragPosition?.pageX ?? 0)) <= maximumClickDragDelta && Math.abs(e.pageY - (initialDragPosition?.pageY ?? 0)) <= maximumClickDragDelta;
    if(!isDragging || isWithinClickDragDelta) {
      onClick(element);
    }
  };

  // Use our tracking system to guarantee removal of ALL pointerup event listeners
  const markerElement = marker.markerDomObject.element;
  
  // Remove all existing pointerup listeners that we've been tracking
  removeAllTrackedEventListeners(markerElement, 'pointerup');
  
  // Add the new event listener using our tracking system
  addTrackedEventListener(markerElement, 'pointerup', onClickListener as EventListener);
  markerElement.style.cursor = 'pointer';
  return {...marker, clickEventListener: onClickListener};
}